import React, { useState, useEffect } from 'react';
import { CreditCard, Banknote, CheckCircle, AlertCircle, ShoppingCart, Users, Copy, Radio, Terminal } from 'lucide-react';
import {
  VentaPuertaRequest,
  AcreditacionResult,
  TerminalPointDto,
  PortadorEntradaDto,
  IntencionPointEstadoDto
} from '../../types/acreditacion.types';
import { acreditacionApi } from '../../services/acreditacionApi';
import { ModalCobroPoint } from './ModalCobroPoint';

interface VentaPuertaFormProps {
  idCongreso: number;
  precioUnitarioFijo?: number;
  onVentaCompletada: (result: AcreditacionResult) => void;
}

export const VentaPuertaForm: React.FC<VentaPuertaFormProps> = ({
  idCongreso,
  precioUnitarioFijo = 35000,
  onVentaCompletada
}) => {
  // Cantidad y Portadores
  const [cantidad, setCantidad] = useState<number>(1);
  const [portadores, setPortadores] = useState<PortadorEntradaDto[]>([
    { nombre: '', apellido: '', dni: '', email: '' }
  ]);

  // Terminales y Medios de Pago
  const [terminales, setTerminales] = useState<TerminalPointDto[]>([]);
  const [idTerminalSeleccionada, setIdTerminalSeleccionada] = useState<number | null>(null);
  const [metodoPago, setMetodoPago] = useState<'EFECTIVO' | 'POINT' | 'CUPON_MANUAL'>('EFECTIVO');
  const [numeroCuponPos, setNumeroCuponPos] = useState<string>('');
  const [autoAcreditar, setAutoAcreditar] = useState<boolean>(true);

  // Modal Point y Estados
  const [intencionActiva, setIntencionActiva] = useState<IntencionPointEstadoDto | null>(null);
  const [modalPointOpen, setModalPointOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar Terminales Point
  useEffect(() => {
    const cargarTerminales = async () => {
      try {
        const data = await acreditacionApi.obtenerTerminalesPoint();
        const activas = Array.isArray(data) ? data.filter((t) => t.activo) : [];
        setTerminales(activas);

        const cachedId = localStorage.getItem('punto_venta_terminal_id');
        if (cachedId && activas.some((t) => t.idTerminalPoint === Number(cachedId))) {
          setIdTerminalSeleccionada(Number(cachedId));
        } else if (activas.length > 0) {
          setIdTerminalSeleccionada(activas[0].idTerminalPoint);
        }
      } catch (err) {
        console.warn('No se pudieron cargar terminales Point:', err);
      }
    };
    cargarTerminales();
  }, []);

  // Actualizar lista de portadores al cambiar cantidad
  const handleCambiarCantidad = (nuevaCantidad: number) => {
    const cant = Math.max(1, Math.min(20, nuevaCantidad));
    setCantidad(cant);
    setPortadores((prev) => {
      const nuevoArray = [...prev];
      if (cant > prev.length) {
        for (let i = prev.length; i < cant; i++) {
          nuevoArray.push({ nombre: '', apellido: '', dni: '', email: '' });
        }
      } else {
        return nuevoArray.slice(0, cant);
      }
      return nuevoArray;
    });
  };

  const handleUpdatePortador = (index: number, field: keyof PortadorEntradaDto, value: string) => {
    setPortadores((prev) => {
      const nuevo = [...prev];
      nuevo[index] = { ...nuevo[index], [field]: value };
      return nuevo;
    });
  };

  const handleCopiarPrimerPortadorATodos = () => {
    if (portadores.length <= 1) return;
    const primero = portadores[0];
    setPortadores(
      portadores.map((p, idx) =>
        idx === 0
          ? p
          : {
              ...p,
              nombre: p.nombre || primero.nombre,
              apellido: p.apellido || primero.apellido,
              email: p.email || primero.email
            }
      )
    );
  };

  const handleSelectTerminal = (id: number) => {
    setIdTerminalSeleccionada(id);
    localStorage.setItem('punto_venta_terminal_id', String(id));
  };

  const totalCalculado = cantidad * precioUnitarioFijo;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validar portadores
    for (let i = 0; i < portadores.length; i++) {
      const p = portadores[i];
      if (!p.nombre.trim() || !p.apellido.trim() || !p.dni.trim()) {
        setError(`Por favor complete Nombre, Apellido y DNI para el Portador #${i + 1}.`);
        return;
      }
    }

    if (metodoPago === 'CUPON_MANUAL' && !numeroCuponPos.trim()) {
      setError('Para cobro con Cupón Manual debe ingresar el número de comprobante/cupón POS.');
      return;
    }

    // Flujo 1: Cobro con Terminal Point Integrado
    if (metodoPago === 'POINT') {
      if (!idTerminalSeleccionada) {
        setError('Debe seleccionar una Terminal Point habilitada para despachar el cobro.');
        return;
      }

      try {
        setLoading(true);
        const intencion = await acreditacionApi.crearIntencionPoint({
          idCongreso,
          idTerminalPoint: idTerminalSeleccionada,
          cantidadEntradas: cantidad,
          portadores: portadores.map((p) => ({
            nombre: p.nombre.trim(),
            apellido: p.apellido.trim(),
            dni: p.dni.trim(),
            email: p.email?.trim() || undefined
          })),
          autoAcreditar
        });

        setIntencionActiva(intencion);
        setModalPointOpen(true);
      } catch (err: any) {
        setError(err?.response?.data?.mensaje || err?.message || 'Error al conectar con la Terminal Point.');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Flujo 2: Efectivo o Cupón Manual
    try {
      setLoading(true);
      const primerPortador = portadores[0];
      const req: VentaPuertaRequest = {
        idCongreso,
        idAfiliadoBase: 0,
        esParaTitular: false,
        nombrePortador: primerPortador.nombre.trim(),
        apellidoPortador: primerPortador.apellido.trim(),
        dniPortador: primerPortador.dni.trim(),
        emailPortador: primerPortador.email?.trim() || undefined,
        monto: totalCalculado,
        refMedioPago: metodoPago === 'CUPON_MANUAL' ? 6 : 1,
        codigoMedioPago: metodoPago === 'CUPON_MANUAL' ? 'POS_MERCADOPAGO' : 'EFECTIVO',
        numeroCuponPos: metodoPago === 'CUPON_MANUAL' ? numeroCuponPos.trim() : undefined,
        autoAcreditar
      };

      const result = await acreditacionApi.registrarVentaPuerta(req);
      onVentaCompletada(result);

      // Reset
      handleCambiarCantidad(1);
      setPortadores([{ nombre: '', apellido: '', dni: '', email: '' }]);
      setNumeroCuponPos('');
    } catch (err: any) {
      setError(err?.response?.data?.mensaje || err?.message || 'Error registrando la venta en puerta.');
    } finally {
      setLoading(false);
    }
  };

  const handlePointAprobado = (res: IntencionPointEstadoDto) => {
    setModalPointOpen(false);
    setIntencionActiva(null);

    const resultadoAcred: AcreditacionResult = {
      estadoCodigo: 1,
      mensaje: `Cobro Point aprobado ($${res.monto.toLocaleString('es-AR')}). Entradas emitidas con éxito.`,
      numeroEntrada: res.paymentId ? `MP-${res.paymentId}` : undefined,
      nombrePortador: portadores.map((p) => `${p.nombre} ${p.apellido}`).join(', '),
      dniPortador: portadores.map((p) => p.dni).join(', ')
    };

    onVentaCompletada(resultadoAcred);

    // Reset
    handleCambiarCantidad(1);
    setPortadores([{ nombre: '', apellido: '', dni: '', email: '' }]);
    setNumeroCuponPos('');
  };

  return (
    <div className="card-blueprint rounded-3xl p-6 sm:p-8 max-w-2xl mx-auto shadow-2xl">
      {/* Encabezado */}
      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-700/50">
        <div className="w-12 h-12 rounded-2xl bg-brand-surface border border-brand-cyan/40 flex items-center justify-center text-brand-cyan shadow-glow-cyan">
          <ShoppingCart className="w-6 h-6" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">Venta Presencial en Puerta</h3>
          <p className="text-xs text-slate-400">
            Tarifa oficial: <span className="text-brand-sky font-bold font-mono">${precioUnitarioFijo.toLocaleString('es-AR')}</span> por entrada.
          </p>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-red-950/60 border border-red-500/50 text-red-200 text-xs flex items-center gap-3 mb-6">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Selector de Cantidad */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-slate-300 uppercase tracking-wider">
              Cantidad de Entradas
            </label>
            <span className="text-xs text-brand-cyan font-semibold">
              Total: <strong className="text-white text-sm">${totalCalculado.toLocaleString('es-AR')}</strong>
            </span>
          </div>

          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => handleCambiarCantidad(num)}
                className={`flex-1 py-2.5 rounded-xl font-heading font-extrabold text-sm transition-all border ${
                  cantidad === num
                    ? 'bg-brand-cyan text-brand-dark border-brand-cyan shadow-glow-cyan'
                    : 'bg-brand-surface border-brand-cyan/20 text-slate-300 hover:text-white hover:border-brand-cyan/40'
                }`}
              >
                {num}
              </button>
            ))}
            <div className="w-20">
              <input
                type="number"
                min="1"
                max="50"
                value={cantidad}
                onChange={(e) => handleCambiarCantidad(Number(e.target.value))}
                className="w-full text-center py-2.5 rounded-xl bg-brand-dark border border-brand-cyan/30 text-white text-sm font-bold focus:border-brand-cyan focus:outline-none"
                title="Cantidad manual"
              />
            </div>
          </div>
        </div>

        {/* Datos de los Portadores */}
        <div className="space-y-4 pt-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-300 uppercase tracking-wider">
              <Users className="w-4 h-4 text-brand-cyan" />
              <span>Datos de Asistentes ({cantidad})</span>
            </div>
            {cantidad > 1 && (
              <button
                type="button"
                onClick={handleCopiarPrimerPortadorATodos}
                className="text-[11px] text-brand-sky hover:text-white flex items-center gap-1 transition-colors"
                title="Copiar datos comunes del 1° asistente a los demás"
              >
                <Copy className="w-3.5 h-3.5" />
                <span>Copiar del 1° a todos</span>
              </button>
            )}
          </div>

          <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {portadores.map((p, idx) => (
              <div
                key={idx}
                className="p-3.5 rounded-2xl bg-brand-surface/70 border border-slate-700/60 space-y-3"
              >
                <div className="text-[11px] font-bold text-brand-sky uppercase tracking-wider">
                  Asistente #{idx + 1}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    required
                    value={p.nombre}
                    onChange={(e) => handleUpdatePortador(idx, 'nombre', e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-brand-dark border border-brand-cyan/30 text-white text-xs focus:border-brand-cyan focus:outline-none"
                    placeholder="Nombre del asistente"
                  />
                  <input
                    type="text"
                    required
                    value={p.apellido}
                    onChange={(e) => handleUpdatePortador(idx, 'apellido', e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-brand-dark border border-brand-cyan/30 text-white text-xs focus:border-brand-cyan focus:outline-none"
                    placeholder="Apellido del asistente"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    required
                    value={p.dni}
                    onChange={(e) => handleUpdatePortador(idx, 'dni', e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-brand-dark border border-brand-cyan/30 text-white text-xs font-mono focus:border-brand-cyan focus:outline-none"
                    placeholder="DNI del asistente"
                  />
                  <input
                    type="email"
                    value={p.email || ''}
                    onChange={(e) => handleUpdatePortador(idx, 'email', e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-brand-dark border border-brand-cyan/30 text-white text-xs focus:border-brand-cyan focus:outline-none"
                    placeholder="email@example.com (opcional)"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Medio de Pago */}
        <div className="space-y-3 pt-2">
          <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
            Medio de Cobro
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => setMetodoPago('EFECTIVO')}
              className={`p-3.5 rounded-2xl border flex flex-col items-center gap-1.5 transition-all ${
                metodoPago === 'EFECTIVO'
                  ? 'bg-brand-cyan/20 border-brand-cyan text-white shadow-glow-cyan'
                  : 'bg-brand-surface border-brand-cyan/20 text-slate-400 hover:text-white'
              }`}
            >
              <Banknote className="w-5 h-5 text-brand-cyan" />
              <span className="font-heading font-bold text-xs">Efectivo</span>
            </button>

            <button
              type="button"
              onClick={() => setMetodoPago('POINT')}
              className={`p-3.5 rounded-2xl border flex flex-col items-center gap-1.5 transition-all ${
                metodoPago === 'POINT'
                  ? 'bg-brand-cyan/20 border-brand-cyan text-white shadow-glow-cyan'
                  : 'bg-brand-surface border-brand-cyan/20 text-slate-400 hover:text-white'
              }`}
            >
              <CreditCard className="w-5 h-5 text-brand-sky" />
              <span className="font-heading font-bold text-xs">Terminal Point</span>
            </button>

            <button
              type="button"
              onClick={() => setMetodoPago('CUPON_MANUAL')}
              className={`p-3.5 rounded-2xl border flex flex-col items-center gap-1.5 transition-all ${
                metodoPago === 'CUPON_MANUAL'
                  ? 'bg-amber-500/20 border-amber-500 text-white'
                  : 'bg-brand-surface border-brand-cyan/20 text-slate-400 hover:text-white'
              }`}
            >
              <Radio className="w-5 h-5 text-amber-400" />
              <span className="font-heading font-bold text-xs">Cupón Manual</span>
            </button>
          </div>

          {/* Selector de Terminal Point si está seleccionado POINT */}
          {metodoPago === 'POINT' && (
            <div className="p-4 rounded-2xl bg-brand-surface/80 border border-brand-sky/40 space-y-2">
              <label className="text-xs font-semibold text-brand-sky uppercase tracking-wider flex items-center gap-2">
                <Terminal className="w-4 h-4" />
                <span>Seleccionar Terminal Point *</span>
              </label>

              {terminales.length > 0 ? (
                <select
                  value={idTerminalSeleccionada || ''}
                  onChange={(e) => handleSelectTerminal(Number(e.target.value))}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-brand-dark border border-brand-cyan/40 text-white text-xs font-semibold focus:outline-none focus:border-brand-cyan"
                >
                  {terminales.map((t) => (
                    <option key={t.idTerminalPoint} value={t.idTerminalPoint}>
                      {t.nombre} ({t.deviceId}) {t.ubicacion ? `- ${t.ubicacion}` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="text-xs text-amber-300">
                  No se detectaron terminales Point activas. Puede usar el modo Cupón Manual como contingencia.
                </div>
              )}
            </div>
          )}

          {/* Input Cupón Manual si está seleccionado CUPON_MANUAL */}
          {metodoPago === 'CUPON_MANUAL' && (
            <div className="p-4 rounded-2xl bg-brand-surface/80 border border-amber-500/40 space-y-2">
              <label className="block text-xs font-semibold text-amber-300 uppercase tracking-wider">
                Número de Cupón / Comprobante POS *
              </label>
              <input
                type="text"
                required
                value={numeroCuponPos}
                onChange={(e) => setNumeroCuponPos(e.target.value)}
                placeholder="Ej: CUP-MP-849201"
                className="w-full px-3.5 py-2.5 rounded-xl bg-brand-dark border border-amber-500/40 text-white font-mono text-xs focus:outline-none focus:border-amber-400"
              />
              <p className="text-[11px] text-slate-400">
                Ingrese el código de transacción impreso por el dispositivo POS en caso de corte o contingencia.
              </p>
            </div>
          )}
        </div>

        {/* Auto Acreditar Toggle */}
        <div className="flex items-center justify-between p-3.5 rounded-2xl bg-brand-surface/50 border border-slate-700/60">
          <div className="text-xs text-slate-200">
            <span className="font-semibold">Acreditar ingreso en el acto</span>
            <p className="text-[11px] text-slate-400">Habilita el ingreso inmediato sin requerir escaneo de QR posterior.</p>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={autoAcreditar}
            onClick={() => setAutoAcreditar(!autoAcreditar)}
            className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors ${
              autoAcreditar ? 'bg-brand-cyan' : 'bg-slate-700'
            }`}
          >
            <div
              className={`bg-brand-dark w-4 h-4 rounded-full shadow-md transform transition-transform ${
                autoAcreditar ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {/* Botón de Confirmación */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 rounded-xl bg-brand-cyan hover:bg-brand-sky text-brand-dark font-heading font-extrabold text-sm uppercase tracking-wider transition-all shadow-glow-cyan disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <CheckCircle className="w-5 h-5" />
          <span>
            {loading
              ? 'Procesando...'
              : metodoPago === 'POINT'
              ? `Cobrar $${totalCalculado.toLocaleString('es-AR')} en Terminal`
              : `Confirmar Venta ($${totalCalculado.toLocaleString('es-AR')})`}
          </span>
        </button>
      </form>

      {/* Modal Interactivo de Cobro Point */}
      <ModalCobroPoint
        isOpen={modalPointOpen}
        intencion={intencionActiva}
        onAprobado={handlePointAprobado}
        onCancelado={() => {
          setModalPointOpen(false);
          setIntencionActiva(null);
        }}
        onFallbackManual={() => {
          setModalPointOpen(false);
          setIntencionActiva(null);
          setMetodoPago('CUPON_MANUAL');
        }}
      />
    </div>
  );
};
